/** @jsx isolateComponent */
import { css } from '@emotion/core';

import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { Colors } from '../../../theme/Colors';
import { Icon } from '../../display/Icon';
import { Input } from '../../forms/Input';

import { DateRangePickerValue } from './DateRangePickerValue';
import { DateOption, DateRange } from './types';

type Props = {
  /** Callback to clear the selected value */
  clearSelection: () => void;
  /** HTML `id` attribute for the element, used to associate it with a `label`. */
  id?: string;
  /** Disables the input and makes it non-interactive. */
  isDisabled?: boolean;
  /** Indicates the field has an error */
  isErrored?: boolean;
  /** Is menu open */
  isOpen: boolean;
  /** Callback for when the input is clicked. */
  onClick?: () => void;
  /** an optional list of predefined DateRanges the user can select from */
  options: Array<DateOption>;
  /** A hint to the user of what can be entered in the control. */
  placeholder?: string;
  /** rendered size of the input */
  size: 'small' | 'medium';
  /** value of the input */
  value: DateRange;
};

const Styles = labelStyles('DateRangePickerInput', {
  root: (props: Props) => css`
    cursor: ${props.isDisabled ? 'default' : 'pointer'};
  `,

  button: css`
    display: flex;
    align-items: center;
    min-width: 0;
  `,

  clearSelectionButton: css`
    align-items: center;
    cursor: pointer;
    display: flex;
    height: 100%;
    justify-content: center;
    width: 100%;
  `,

  placeholder: css`
    color: ${Colors.gray60};
    display: block;
    overflow: hidden;
    white-space: nowrap;
  `,
});

/**
 * The icon which sits inside the input field.
 */
const ClearSelectionButton = ({
  clearSelection,
  isFocused,
  isHovered,
}: {
  clearSelection: () => void;
  isFocused: boolean;
  isHovered: boolean;
}) => {
  // If not focused or hovered it should be a specific color,
  // otherwise it should inherit its color.
  const iconColor = !(isFocused || isHovered) ? 'grey500' : undefined;
  return (
    <button
      type="button"
      css={Styles.clearSelectionButton}
      onClick={clearSelection}
      title="Clear Selection"
    >
      <Icon
        canAcceptPointerEvents={false}
        color={iconColor}
        name="x"
        size={20}
      />
    </button>
  );
};

export const DateRangePickerInput = (props: Props) => {
  const { value, clearSelection } = props;
  const { from, to } = value;
  const hasValue = Boolean(from || to);
  const showClearSelectionButton = hasValue && !props.isDisabled;

  return (
    <div css={Styles.root(props)}>
      <Input
        id={props.id}
        isDisabled={props.isDisabled}
        isErrored={props.isErrored}
        renderIconAfter={({
          isFocused,
          isHovered,
        }: {
          isFocused: boolean;
          isHovered: boolean;
        }) =>
          showClearSelectionButton && (
            <ClearSelectionButton
              clearSelection={clearSelection}
              isFocused={isFocused}
              isHovered={isHovered}
            />
          )
        }
        renderIconBefore={({ iconSize }: { iconSize: number }) => (
          <Icon name="actionCalendar" size={iconSize} />
        )}
        size={props.size}
      >
        {(inputProps: any) => (
          // Intentionally `button` and not `Button` since it looks
          // and behaves differently.
          // eslint-disable-next-line jsx-a11y/role-supports-aria-props
          <button
            aria-controls={inputProps['aria-controls']}
            aria-expanded={props.isOpen ? 'true' : 'false'}
            aria-haspopup="true"
            aria-invalid={inputProps['aria-invalid']}
            aria-label={inputProps['aria-label']}
            css={[inputProps.css, Styles.button]}
            disabled={inputProps.disabled}
            id={inputProps.id}
            onClick={props.onClick}
            onKeyDown={inputProps.onKeyDown}
            type="button"
          >
            {hasValue ? (
              <DateRangePickerValue
                options={props.options}
                value={props.value}
              />
            ) : (
              <span css={Styles.placeholder}>{props.placeholder}</span>
            )}
          </button>
        )}
      </Input>
    </div>
  );
};
